import {
  IBatchResponse,
  IExtractRegionResponse,
  IRegionRequest,
  IRegionResponse,
  IRegionUpdateRequest,
} from '../../types/API/Region';
import { setIsDrawing } from '../features/draw/draw-slice';
import { setUserTiles } from '../features/region/region-slice';
import { setMessages } from '../features/socket/socket-slice';
import { resetUIState } from '../features/ui/ui-slice';
import { RootState } from '../store';
import { baseApi } from './index';

export const regionApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['SavedRegions'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      addRegion: builder.mutation<IRegionResponse, IRegionRequest>({
        query(body) {
          return {
            url: '/regions/add',
            method: 'POST',
            body,
          };
        },
        invalidatesTags: ['SavedRegions'],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          await queryFulfilled;
          dispatch(setIsDrawing(false));
        },
      }),
      batchAddRegions: builder.mutation<IBatchResponse, IRegionRequest[]>({
        query(body) {
          return {
            url: '/regions/add/batch',
            method: 'POST',
            body,
          };
        },
        invalidatesTags: ['SavedRegions'],
      }),
      updateRegion: builder.mutation<
        IRegionResponse,
        {
          id: string;
          body: IRegionUpdateRequest;
        }
      >({
        query({ body, id }) {
          return {
            url: `/regions/update/${id}`,
            method: 'PATCH',
            body,
          };
        },
        invalidatesTags: ['SavedRegions'],
      }),
      removeRegion: builder.mutation<{ success: boolean }, { id: string }>({
        query({ id }) {
          return {
            url: `/regions/remove/${id}`,
            method: 'DELETE',
          };
        },
        async onQueryStarted({ id }, { queryFulfilled, dispatch, getState }) {
          await queryFulfilled;
          const newMessages = (
            getState() as RootState
          ).socketState.messages.filter(
            (message) => message?.payload?.region?.id !== id
          );
          dispatch(setMessages(newMessages));
        },
        invalidatesTags: ['SavedRegions'],
      }),
      getUserRegions: builder.query<IRegionResponse[], void>({
        query() {
          return {
            url: '/regions/user',
            method: 'GET',
          };
        },
        providesTags: ['SavedRegions'],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          const response = await queryFulfilled;
          dispatch(setUserTiles(response.data));

          if (response.data.length === 0) {
            dispatch(resetUIState());
          }
        },
      }),

      extractPolygonsFromFile: builder.mutation<
        IExtractRegionResponse,
        FormData
      >({
        query(body) {
          return {
            url: '/regions/extract-polygons',
            method: 'POST',
            body,
          };
        },
      }),
    }),
  });

export const {
  useAddRegionMutation,
  useBatchAddRegionsMutation,
  useUpdateRegionMutation,
  useRemoveRegionMutation,
  useGetUserRegionsQuery,
  useExtractPolygonsFromFileMutation,
} = regionApi;
